// src/components/common/OccurrencePreview.js
import React, { useState, useEffect } from 'react';
import { format, addDays, parseISO, isAfter, isBefore } from 'date-fns';
import { lv, enUS, ru } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaCalendarAlt } from 'react-icons/fa';
import { FEATURES } from '../../config/features';
import './OccurrencePreview.css';

/**
 * Component to preview which dates will be created for a recurring post
 *
 * @param {Object} props
 * @param {Date|string} props.startDate - Start date for recurrence
 * @param {Date|string} props.endDate - End date for recurrence (optional)
 * @param {Array<number>} props.weekdays - Array of ISO weekday numbers (1-7)
 * @param {Array<string>} props.excludedDates - Array of date strings to exclude
 * @param {number} props.advanceLimit - User's advance booking limit in days
 * @param {function} props.onOccurrenceCountChange - Callback with occurrence count
 */
const OccurrencePreview = ({
  startDate,
  endDate = null,
  weekdays = [],
  excludedDates = [],
  advanceLimit = 3,
  onOccurrenceCountChange
}) => {
  const { t, i18n } = useTranslation('common');
  // Localize the generated date list (day/month names) to the UI language —
  // date-fns defaults to English otherwise.
  const dateLocale = i18n.language === 'lv' ? lv : i18n.language === 'ru' ? ru : enUS;
  const [occurrences, setOccurrences] = useState([]);
  const [limitedByPlan, setLimitedByPlan] = useState(false);

  useEffect(() => {
    if (!startDate || weekdays.length === 0) {
      setOccurrences([]);
      setLimitedByPlan(false);
      return;
    }

    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate effective end date
    const planLimitDate = addDays(today, advanceLimit);
    const userEndDate = endDate ? (typeof endDate === 'string' ? parseISO(endDate) : endDate) : null;

    // Use earliest of: user's end date, plan limit, or default 90 days
    let effectiveEnd = planLimitDate;
    if (userEndDate) {
      effectiveEnd = isBefore(userEndDate, planLimitDate) ? userEndDate : planLimitDate;
    }

    const limited = userEndDate ? isAfter(userEndDate, planLimitDate) : false;

    const dates = [];
    let currentDate = start;

    // Generate occurrences
    while (isBefore(currentDate, effectiveEnd) || currentDate.getTime() === effectiveEnd.getTime()) {
      const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay(); // Convert to ISO (1=Mon, 7=Sun)

      if (weekdays.includes(dayOfWeek)) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        if (!excludedDates.includes(dateStr)) {
          dates.push({
            date: new Date(currentDate),
            dateStr: dateStr
          });
        }
      }

      currentDate = addDays(currentDate, 1);

      // Safety limit
      if (dates.length >= 100) break;
    }

    setOccurrences(dates);
    setLimitedByPlan(limited);
  }, [
    startDate?.toISOString?.() || startDate,
    endDate?.toISOString?.() || endDate,
    weekdays.join(','),
    excludedDates.join(','),
    advanceLimit
  ]);

  const getStatusIcon = () => {
    if (weekdays.length === 0) {
      return <FaExclamationTriangle className="ocp-icon ocp-warning" />;
    }
    if (occurrences.length === 0) {
      return <FaExclamationTriangle className="ocp-icon ocp-warning" />;
    }
    return <FaCheckCircle className="ocp-icon ocp-success" />;
  };

  const getStatusMessage = () => {
    if (weekdays.length === 0) {
      return t('recurring.no_days_selected');
    }
    if (occurrences.length === 0) {
      return t('recurring.no_occurrences_warning');
    }
    return t('recurring.occurrences_will_be_created', { count: occurrences.length });
  };

  return (
    <div className="ocp-container">
      {/* Header */}
      <div className="ocp-header">
        <div className="ocp-status">
          {getStatusIcon()}
          <span className="ocp-status-text">{getStatusMessage()}</span>
        </div>
      </div>

      {/* Plan Limit Warning — only meaningful while paid plans exist. Hidden
          during the subscription-free launch (everyone is on the top plan). */}
      {FEATURES.PAYMENTS_ENABLED && limitedByPlan && (
        <div className="ocp-warning-box">
          <FaInfoCircle className="ocp-warning-icon" />
          <div className="ocp-warning-text">
            <p className="ocp-warning-title">{t('recurring.limited_by_plan')}</p>
            <p className="ocp-warning-desc">
              {t('recurring.limited_by_plan_desc', { days: advanceLimit })}
            </p>
          </div>
        </div>
      )}

      {/* Occurrence List */}
      {occurrences.length > 0 && (
        <div className="ocp-list-container">
          <div className="ocp-list-header">
            <FaCalendarAlt className="ocp-list-icon" />
            <span>
              {occurrences.length > 10
                ? t('recurring.showing_first_dates', { count: 10 })
                : t('recurring.all_dates')}
            </span>
          </div>

          <div className="ocp-list">
            {occurrences.slice(0, 10).map((occ, idx) => (
              <div key={idx} className="ocp-list-item">
                <span className="ocp-item-number">{idx + 1}</span>
                <span className="ocp-item-date">
                  {format(occ.date, 'EEE, MMM dd, yyyy', { locale: dateLocale })}
                </span>
                <FaCheckCircle className="ocp-item-check" />
              </div>
            ))}
          </div>

          {occurrences.length > 10 && (
            <div className="ocp-more-indicator">
              {t('recurring.and_more_dates', { count: occurrences.length - 10 })}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {weekdays.length > 0 && occurrences.length === 0 && (
        <div className="ocp-empty">
          <FaExclamationTriangle className="ocp-empty-icon" />
          <p>{t('recurring.adjust_settings')}</p>
        </div>
      )}
    </div>
  );
};

export default OccurrencePreview;
